import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {createContext, useCallback, useContext, useRef} from 'react'
import SockJsClient from 'react-stomp'
import {API_BASE_URL, SystemRole} from '../Constants'
import {useAuth} from '../components/authentication/AuthProvider'
import {
    clearStorage,
    getClientId,
    getHospitalSelect,
    getHospitalsBelong,
    getSessionId,
    getUserId,
    getUsername,
    setHospitalLogo,
    setHospitalName,
    setHospitalSelect,
    setHospitalsBelong,
} from '../components/authentication/AuthUtils'
import {reloadHospitalSelect} from '../components/authentication/HospitalSelectEvent'
import {createOrUpdateChatGenericInfo} from '../components/chat/ChatApi'
import {onNewChatMessage} from '../components/chat/ChatService'
import {
    clientEventTypes,
    onClientDetailChangeListener,
    onClientTableChangeListener,
} from '../components/client/broadcast/ClientEvent'
import {
    onSystemConfigurationChangeListener,
    systemConfigurationTypes,
} from '../components/configuration/broadcast/GenericLogEvent'
import {onGenericLogTableChangeListener} from '../components/generic-log/broadcast/GenericLogEvent'
import {onAppNotificationListener} from '../components/notification/event/NotificationEvent'
import {onStaticListTableChangeListener} from '../components/static-list/broadcast/StaticListEvent'
import {tableTypes} from '../components/styled-component/StyledTableUtils'
import {onNotifyImportResultListener} from '../components/styled-component/broadcast/FileEvent'
import {
    generalPractitionerEventTypes,
    onGeneralPractitionerTableChangeListener,
} from '../components/styled-component/general-practitioner/broadcast/GeneralPractitionerEvent'
import {
    healthCareEventTypes,
    onHealthCareRoleChangeListener,
    onHealthCareTableChangeListener,
    onHealthCareUserManagementChangeListener,
} from '../components/styled-component/hc-practitioner/broadcast/HealthCarePractitionerEvent'
import {
    hospitalEventTypes,
    onHospitalDetailChangeListener,
    onHospitalTableChangeListener,
} from '../components/styled-component/hospital/broadcast/HospitalEvent'
import {
    onPathwayTableChangeListener,
    pathwayEventTypes,
} from '../components/styled-component/pathway/broadcast/PathwayEvent'
import {
    onPatientPathwayChangeListener,
    onPatientStaticListChangeListener,
    onPatientTableChangeListener,
    patientEventTypes,
} from '../components/styled-component/patient/broastcast/PatientEvent'
import {
    calendarItemEventTypes,
    onCalendarItemTableChangeListener,
} from '../components/styled-component/reservations/broadcast/CalendarItemEvent'
import {
    onSupportTeamTableChangeListener,
    onUserSupportTeamDetailChangeListener,
    supportTeamEventTypes,
} from '../components/styled-component/support-team/broadcast/SupportTeamEvent'
import {useAppConfig} from '../context/app/AppContext'
import {log} from '../log/Logs'
import {QueryKeyTypes} from '../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../query-manager/QueryKeysManager'
import {onWifiConnectionReload} from '../utils/DetailPageReloadUtils'
import {openPage} from '../utils/NavigatorUtils'
import {reloadDataType} from './WebsocketConstants'
const WebsocketContext = createContext({})

const WEBSOCKET_ENDPOINT = `${API_BASE_URL}`
const DESTINATION_PREFIX = '/ws/app'
const MessageQueryKey = 'ChatMessages'
export const WebsocketProvider = ({children}) => {
    const websocketRef = useRef(null)
    const userId = getUserId()
    const {userRole} = useAppConfig()
    const queryClient = useQueryClient()
    const {user} = useAuth()

    const handleOnMessageCallback = useCallback(
        payload => {
            if (!!payload) {
                switch (payload?.type) {
                    case 'chat_message':
                        if (payload) {
                            let holderData = {...payload}
                            if (
                                payload?.messageType === 'image' &&
                                !!payload?.images
                            ) {
                                holderData.data = holderData?.images ?? []
                            }

                            onNewChatMessage(holderData)
                        }

                        return
                    case 'notification':
                        // TODO handle notification messages
                        return
                    case 'kick_out':
                        if (!payload?.sessionId) {
                            clearStorage()
                            openPage('/')
                            return
                        } else {
                            if (payload?.sessionId) {
                                if (payload?.sessionId !== getSessionId()) {
                                    clearStorage()
                                    openPage('/')
                                    return
                                }
                            }
                        }
                        break
                    case 'access_denied':
                        if (userRole === SystemRole.USER) {
                            clearStorage()
                            openPage('/')
                            return
                        }
                        break
                    case 'reload_data':
                        let tableRecordData = null

                        try {
                            tableRecordData = JSON.parse(payload?.data)
                        } catch (e) {
                            tableRecordData = {}
                        }

                        switch (payload?.reloadDataType) {
                            // CLIENT
                            case reloadDataType.client:
                                if (
                                    !payload?.userId ||
                                    payload?.userId === user?.id
                                ) {
                                    return
                                }

                                onClientTableChangeListener(
                                    clientEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )

                                onClientDetailChangeListener(
                                    clientEventTypes.DETAIL_CHANGE,
                                    tableRecordData,
                                )

                                break

                            // HOSPITAL
                            case reloadDataType.hospital:
                                if (
                                    !payload?.userId ||
                                    payload?.userId === user?.id
                                ) {
                                    return
                                }

                                onHospitalTableChangeListener(
                                    hospitalEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )

                                onHospitalDetailChangeListener(
                                    hospitalEventTypes.DETAIL_CHANGE,
                                    tableRecordData,
                                )

                                break

                            // PATIENT
                            case reloadDataType.user:
                                onPatientTableChangeListener(
                                    patientEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            case reloadDataType.userPathway:
                                onPatientPathwayChangeListener(
                                    patientEventTypes.PATHWAY_CHANGE,
                                    tableRecordData,
                                )
                                break
                            case reloadDataType.userStaticList:
                                onPatientStaticListChangeListener(
                                    patientEventTypes.STATIC_LIST_CHANGE,
                                    tableRecordData,
                                )
                                break
                            case reloadDataType.userSupportTeam:
                                onUserSupportTeamDetailChangeListener(
                                    supportTeamEventTypes.USER_SUPPORT_TEAM_CHANGE,
                                    tableRecordData,
                                )

                                onPatientStaticListChangeListener(
                                    patientEventTypes.SUPPORT_TEAM_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // HCP
                            case reloadDataType.hcp:
                                onHealthCareTableChangeListener(
                                    healthCareEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            case reloadDataType.hcpRole:
                                onHealthCareRoleChangeListener(
                                    healthCareEventTypes.ROLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            case reloadDataType.userManagement:
                                onHealthCareUserManagementChangeListener(
                                    healthCareEventTypes.USER_MANAGEMENT_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // GENERAL PRACTITIONER
                            case reloadDataType.gp:
                                onGeneralPractitionerTableChangeListener(
                                    generalPractitionerEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // PATHWAY
                            case reloadDataType.pathway:
                                onPathwayTableChangeListener(
                                    pathwayEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // SUPPORT TEAM
                            case reloadDataType.supportTeam:
                                onSupportTeamTableChangeListener(
                                    supportTeamEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // STATIC LIST
                            case reloadDataType.staticList:
                                onStaticListTableChangeListener(
                                    supportTeamEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // NOTIFICATIONS
                            case reloadDataType.notification:
                                onAppNotificationListener(tableRecordData)
                                break
                            // CALENDAR ITEM
                            case reloadDataType.calendarItem:
                                onCalendarItemTableChangeListener(
                                    calendarItemEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // GENERIC LOG
                            case reloadDataType.genericLog:
                                onGenericLogTableChangeListener(
                                    generalPractitionerEventTypes.TABLE_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // SYSTEM CONFIGURATION
                            case reloadDataType.systemConfiguration:
                                onSystemConfigurationChangeListener(
                                    systemConfigurationTypes.ADDITIONAL_METHOD_CHANGE,
                                    tableRecordData,
                                )
                                break
                            // TIMEZONE
                            // APP CONFIGURATION
                            case reloadDataType.timezone:
                            case reloadDataType.appConfiguration:
                                const {appConfigKeys} = QueryKeyManager(
                                    QueryKeyTypes.appConfiguration,
                                    user?.id,
                                )

                                queryClient.invalidateQueries(
                                    appConfigKeys.pattern(),
                                )

                                // Reload temporary
                                // window.location.reload()

                                break

                            default:
                                break
                        }

                        // if (payload?.reloadActionType === 'refetch') {
                        //     const queryKey = getQueryKeyFromReloadDataType(
                        //         payload?.reloadDataType,
                        //     )

                        //     if (payload?.reloadDataType === 'user') {
                        //         refetchCurrentData(queryKey)
                        //         refetchCurrentData('Hospital')
                        //         refetchCurrentData('HCP')
                        //         refetchIds(payload?.data, 'HCP-Patients')
                        //     } else if (payload?.reloadDataType === 'gp') {
                        //         refetchCurrentData('General-Practitioner')
                        //     } else if (payload?.reloadDataType === 'summary') {
                        //         refetchCurrentData('Summary' + payload?.userId)
                        //     } else if (
                        //         payload?.reloadDataType === 'userManagement'
                        //     ) {
                        //         refetchCurrentData('Patient-HCPs-' + userId)
                        //         refetchCurrentData('Patient')
                        //     } else if (
                        //         payload?.reloadDataType === 'conversation' &&
                        //         payload?.userId === userId
                        //     ) {
                        //         refetchCurrentData(
                        //             'Conversation-' + payload?.userId,
                        //         )
                        //     } else if (
                        //         payload?.reloadDataType === 'healthActivity'
                        //     ) {
                        //         refetchCurrentData(
                        //             'Health-Activity-' + payload?.userId,
                        //         )
                        //     } else if (
                        //         payload?.reloadDataType === 'watchConfig' &&
                        //         payload.userId !== userId
                        //     ) {
                        //         refetchCurrentData(
                        //             'Watch-Config' + payload.userId,
                        //         )
                        //     } else if (payload?.reloadDataType === 'hcpRole') {
                        //         refetchCurrentData('HCP-Roles')
                        //         refetchCurrentData('HCP-Detail')
                        //         reloadHospitalSelect()
                        //     } else if (
                        //         payload?.reloadDataType === 'notification'
                        //     ) {
                        //         refetchEvent.emit(
                        //             'REFETCH-NOTIFICATION-WITH-SETTINGS',
                        //         )
                        //     } else if (payload?.reloadDataType === 'pathway') {
                        //         refetchCurrentData(
                        //             'Symptom-By-Pathway' + userId,
                        //         )
                        //     } else if (
                        //         payload?.reloadDataType === 'genericLog'
                        //     ) {
                        //         refetchCurrentData('Generic-Log')
                        //     } else {
                        //         refetchCurrentData(queryKey)
                        //     }
                        // } else {
                        //     if (
                        //         payload?.reloadDataType === 'staticList' &&
                        //         payload?.data
                        //     ) {
                        //         reloadDataList(
                        //             'staticList-' + payload?.userId,
                        //             JSON.parse(payload?.data),
                        //         )
                        //     } else if (
                        //         payload?.reloadDataType === 'userPathway' ||
                        //         payload?.reloadDataType === 'ownedPathway' ||
                        //         payload?.reloadDataType === 'ownedHCP' ||
                        //         payload?.reloadDataType === 'hospital' ||
                        //         payload?.reloadDataType === 'conversation' ||
                        //         payload?.reloadDataType === 'pathway' ||
                        //         payload?.reloadDataType === 'examination'
                        //     ) {
                        //         const queryKey = getQueryKeyFromReloadDataType(
                        //             payload?.reloadDataType,
                        //         )

                        //         // Patient Dashboard
                        //         if (
                        //             (payload?.reloadDataType ===
                        //                 'ownedPathway' ||
                        //                 payload?.reloadDataType ===
                        //                     'ownedHCP') &&
                        //             userId === payload?.userId &&
                        //             payload?.data
                        //         ) {
                        //             refetchCurrentData(
                        //                 queryKey +
                        //                     '-' +
                        //                     userId +
                        //                     '-' +
                        //                     JSON.parse(payload?.data),
                        //             )
                        //         } else {
                        //             if (
                        //                 payload?.reloadDataType ===
                        //                     'conversation' &&
                        //                 userId === payload?.userId
                        //             ) {
                        //                 refetchCurrentData(
                        //                     queryKey + '-' + userId,
                        //                 )
                        //             } else {
                        //                 refetchCurrentData(queryKey)
                        //             }
                        //         }
                        //     } else {
                        //         const payloadData =
                        //             !!payload?.data &&
                        //             payload?.reloadDataType !== 'timezone'
                        //                 ? JSON.parse(payload?.data)
                        //                 : null

                        //         if (payload?.reloadDataType === 'ecg') {
                        //             const queryKey =
                        //                 getQueryKeyFromReloadDataType(
                        //                     payload?.reloadDataType,
                        //                 )

                        //             onDetailInformationReload(
                        //                 queryKey + payloadData?.userId,
                        //                 payloadData ? payloadData : null,
                        //             )
                        //         } else if (
                        //             payload?.reloadDataType === 'userManagement'
                        //         ) {
                        //             reloadDataTable(
                        //                 'HCP-Patients' + payloadData?.mainRefId,
                        //                 payloadData,
                        //             )

                        //             reloadDataTable(
                        //                 'User-Management' +
                        //                     payloadData?.mainRefId,
                        //                 payloadData,
                        //             )

                        //             refetchCurrentData(
                        //                 'HCP-Patients',
                        //                 payloadData,
                        //             )
                        //         } else if (
                        //             (!!payload?.userId ||
                        //                 payload?.userId === 0) &&
                        //             payload.userId !== userId
                        //         ) {
                        //             const payloadData = !!payload?.data
                        //                 ? JSON.parse(payload?.data)
                        //                 : null

                        //             // For table reload
                        //             const queryKey =
                        //                 getQueryKeyFromReloadDataType(
                        //                     payload?.reloadDataType,
                        //                 )

                        //             if (!!queryKey) {
                        //                 const userIdsListenerKeys = [
                        //                     'Health-Record',
                        //                     'User-Static-List',
                        //                     'Note',
                        //                     'Next-Of-Kin',
                        //                     'UserPathwayDetail',
                        //                     'Electrocardiogram',
                        //                     'Reminder',
                        //                     'Biometrics',
                        //                 ]
                        //                 if (
                        //                     !!userIdsListenerKeys.includes(
                        //                         queryKey,
                        //                     )
                        //                 ) {
                        //                     reloadDataTable(
                        //                         queryKey + payloadData?.userId,
                        //                         payloadData,
                        //                     )
                        //                 } else {
                        //                     if (queryKey === 'Generic-Log') {
                        //                         reloadDataTable(
                        //                             queryKey +
                        //                                 '-' +
                        //                                 payloadData?.type +
                        //                                 '-' +
                        //                                 payloadData?.refId,
                        //                             payloadData,
                        //                         )
                        //                     } else {
                        //                         reloadDataTable(
                        //                             queryKey,
                        //                             payloadData,
                        //                         )
                        //                     }
                        //                 }
                        //             }

                        //             // For detail page reload
                        //             const queryDetailKey =
                        //                 getDetailQueryKeyFromReloadDataType(
                        //                     payload?.reloadDataType,
                        //                 )
                        //             if (!!queryDetailKey) {
                        //                 const userIdsListenerKeys = [
                        //                     'User-Symptom',
                        //                     'HCP-Roles',
                        //                     'Parameter',
                        //                 ]
                        //                 if (
                        //                     userIdsListenerKeys.includes(
                        //                         queryDetailKey,
                        //                     )
                        //                 ) {
                        //                     onDetailInformationReload(
                        //                         queryDetailKey +
                        //                             payloadData?.userId,
                        //                         payloadData
                        //                             ? payloadData
                        //                             : null,
                        //                     )
                        //                 } else {
                        //                     onDetailInformationReload(
                        //                         queryDetailKey +
                        //                             payloadData?.id,
                        //                         payloadData
                        //                             ? payloadData
                        //                             : null,
                        //                     )
                        //                 }
                        //             }
                        //         } else if (
                        //             payload?.reloadDataType === 'timezone'
                        //         ) {
                        //             // reloadTimezone(payload?.data)
                        //             window.location.reload()
                        //         }
                        //     }
                        // }

                        break
                    case 'upload':
                        const payloadUploadData = payload?.data
                            ? JSON.parse(payload?.data)
                            : null

                        onNotifyImportResultListener(
                            tableTypes.patient,
                            payloadUploadData,
                        )
                        break
                    case 'hcp_role_change':
                        const payloadHospitalsBelong = payload?.data
                            ? JSON.parse(payload?.data)
                            : []

                        if (
                            payloadHospitalsBelong &&
                            payloadHospitalsBelong?.length > 0
                        ) {
                            setHospitalsBelong(
                                JSON.stringify(payloadHospitalsBelong),
                            )
                            setHospitalSelect(payloadHospitalsBelong[0]?.id)
                            setHospitalName(payloadHospitalsBelong[0]?.name)
                            setHospitalLogo(
                                payloadHospitalsBelong[0]?.logo || '',
                            )

                            reloadHospitalSelect()
                        }
                        return
                    case 'hospital_change':
                        const hospitalChange = payload?.data
                            ? JSON.parse(payload?.data)
                            : {}

                        if (hospitalChange && hospitalChange?.id) {
                            let hospitalBelongs = !!getHospitalsBelong()
                                ? JSON.parse(getHospitalsBelong())
                                : []
                            const hospitalSelectId = getHospitalSelect() || null

                            if (
                                hospitalBelongs != null &&
                                hospitalBelongs?.length > 0
                            ) {
                                const indexChange = hospitalBelongs.findIndex(
                                    hospital =>
                                        hospital?.id === hospitalChange?.id,
                                )

                                if (indexChange >= 0) {
                                    hospitalBelongs[indexChange] =
                                        hospitalChange

                                    setHospitalsBelong(
                                        JSON.stringify(hospitalBelongs),
                                    )

                                    if (
                                        hospitalSelectId != null &&
                                        hospitalSelectId === hospitalChange?.id
                                    ) {
                                        setHospitalName(
                                            hospitalBelongs[indexChange]?.name,
                                        )
                                        setHospitalLogo(
                                            hospitalBelongs[indexChange]
                                                ?.logo || '',
                                        )
                                    }
                                }
                            }

                            reloadHospitalSelect()
                        }
                        return
                    case 'wifi_connection':
                        if (payload?.reloadDataType === 'wifi_connection') {
                            onWifiConnectionReload(
                                payload?.userId,
                                payload?.data ? JSON.parse(payload?.data) : [],
                            )

                            return
                        }
                        return
                    default:
                        return
                }
            }
        },
        [queryClient, user?.id, userRole],
    )

    const handleSendMessage = async payload => {
        if (!payload) return
        let modifiedPayload = payload
            ? {...payload, clientId: getClientId()}
            : {clientId: getClientId()}
        try {
            websocketRef.current.sendMessage(
                `${DESTINATION_PREFIX}/chat`,
                modifiedPayload ? JSON.stringify(modifiedPayload) : '',
            )
        } catch (e) {
            await createOrUpdateChatGenericInfo(
                'message',
                null,
                modifiedPayload,
            )
        }

        queryClient.setQueryData(
            [MessageQueryKey, user?.id, payload.roomId],
            oldData => {
                let holderList = [...oldData.content] || []
                let item = {
                    id: payload?.id,
                    roomId: payload?.roomId,
                    participantId: payload?.participantId,
                    userId: payload?.userId,
                    avatar: payload?.avatar,
                    fullName: payload?.fullName,
                    data:
                        payload.messageType === 'image'
                            ? payload.images
                            : payload?.data,
                    createdAt: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
                    messageType: payload?.messageType,
                }
                holderList.push({...item, userId: user?.id})
                return {...oldData, content: holderList}
            },
        )
    }

    const handleReadMessage = async payload => {
        if (!payload) return
        let modifiedPayload = payload
            ? {...payload, clientId: getClientId()}
            : {clientId: getClientId()}
        try {
            websocketRef.current.sendMessage(
                `${DESTINATION_PREFIX}/readChatMessage`,
                modifiedPayload ? JSON.stringify(modifiedPayload) : '',
            )
        } catch (e) {}
    }

    return (
        <WebsocketContext.Provider
            value={{
                sendChatMessage: handleSendMessage,
                readChatMessage: handleReadMessage,
            }}>
            {children}
            {log('WebsocketProvider')}
            {!!getUsername() && (
                <SockJsClient
                    heartbeat={3000}
                    url={`${WEBSOCKET_ENDPOINT}/websocket`}
                    headers={{
                        username: `${getUsername()}`,
                    }}
                    autoReconnect={true}
                    topics={[
                        `/topic/reload`,
                        `/topic/${getUsername()}/notify`,
                        `/topic/chat/${getUsername()}`,
                        `/topic/chat/${getUserId()}`,
                        `/topic/kick-out/${getUsername()}`,
                        `/topic/access-denied/${getClientId()}`,
                        `/topic/configuration/${getClientId()}`,

                        `/topic/user`,

                        `/topic/client/${getClientId()}`,
                        `/topic/loggedUser/${getUserId()}`,
                        `/topic/loggedUser/${getUsername()}`,
                    ]}
                    onConnect={() => {
                        console.log('---- Websocket Connected ---- ')
                    }}
                    onDisconnect={() => {
                        console.log('---- Websocket Disconnected ---- ')
                    }}
                    onMessage={msg => {
                        handleOnMessageCallback(msg)
                    }}
                    ref={websocketRef}
                />
            )}
        </WebsocketContext.Provider>
    )
}

export const useWebsocket = () => useContext(WebsocketContext)
